/**
 * SED ↔ Site Context Binding Registry — SED_Amendment_Persistence SA6.
 *
 * Declares the static, per-claim-category mapping from a Site Context
 * claim to one or more SED dot-paths. The registry is the single source
 * of truth for which fields participate in bi-directional sync.
 *
 * Authoring contract:
 *   - This array is a static `const`. There is no runtime configuration
 *     surface — adding a new binding is a code change with a test.
 *   - `toSed(claimValue)` and `toContext(sedValue)` are PURE projections.
 *     No side effects. No I/O. No external state. They must round-trip
 *     for the deepEqual guard in `syncSedContextBindings` to detect a
 *     no-op write — if `toContext(toSed(x))` !== `x` for the values we
 *     care about, the binding will livelock through the comparison.
 *   - Both projections handle `undefined` / `null` by returning
 *     `undefined`. Missing values short-circuit the sync.
 *   - When a claim binds to multiple SED paths, the same `toSed` is
 *     fanned out to each path. `toContext` reads the FIRST path that
 *     has a defined value (paths are ordered most-authoritative-first).
 *
 * Engineering rules (CLAUDE.md §1, §4):
 *   - Unit consistency: SED stores numbers in their native unit (km, °C,
 *     g, acres, etc.). Claims may emit free-form text OR a typed value.
 *     The projection lives here so the rest of the pipeline is unit-pure.
 *   - Conservative: only register bindings where the claim ↔ SED mapping
 *     is unambiguous. Free-form claim text (e.g. "co-located substation")
 *     does NOT round-trip cleanly and is intentionally NOT registered —
 *     the diff tab still surfaces the comparator's MEDIUM/HIGH conflict
 *     for the user to resolve manually, just without auto-sync.
 */
import type { ClaimCategory, ExtractedClaim, SedFieldPath } from '../../types/siteContext';

/**
 * A single entry in the binding table.
 *
 * `contextKey` is the claim category that, when emitted by the
 * extractor, becomes eligible for binding. The extractor's category
 * vocabulary is fixed in `siteContext.ts` (ClaimCategory union) so the
 * binding key is statically checkable.
 */
export interface SedBindingEntry {
  /** Claim category that participates in this binding. */
  contextKey: ClaimCategory;
  /**
   * SED dot-paths the claim's value maps onto. Order matters for the
   * reverse direction — `toContext` reads the first defined path.
   */
  sedPaths: readonly SedFieldPath[];
  /**
   * Project a claim's `claimedValue` onto the SED's native value at
   * `sedPaths`. Returns `undefined` when the claim has no value or the
   * projection isn't defined. Pure.
   */
  toSed?: (claimValue: ExtractedClaim['claimedValue']) => string | number | boolean | undefined;
  /**
   * Project a SED value back onto a claim's `claimedValue`. Returns
   * `undefined` when the SED value is missing / out of range / not
   * representable as a claim value. Pure.
   */
  toContext?: (sedValue: unknown) => ExtractedClaim['claimedValue'];
  /** Human-readable label for the diff-tab row. */
  label: string;
}

/* ── Default identity projections ─────────────────────────────────── */

/** Round-trips a primitive value through Number coercion. */
function identityNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Round-trips a string value, trimming whitespace. */
function identityString(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const s = v.trim();
    return s.length > 0 ? s : undefined;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

/** Round-trips a boolean — accepts "true"/"false" strings as well. */
function identityBoolean(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase().trim();
    if (s === 'true' || s === 'yes') return true;
    if (s === 'false' || s === 'no') return false;
  }
  return undefined;
}

/* ── Registry ─────────────────────────────────────────────────────── */

/**
 * Static binding table. Add new bindings here in lockstep with the
 * comparator + extractor — and add a regression test in
 * `__tests__/sedBindingRegistry.test.ts` that pins the round-trip.
 *
 * Why not every ClaimCategory? Many categories are free-form (regulatory
 * stances, schedule narrative, technology choice) and have no
 * unambiguous SED counterpart. Those still cross-check via the
 * comparator pipeline; only the typed-value bindings auto-sync.
 */
export const SED_BINDING_REGISTRY: readonly SedBindingEntry[] = [
  {
    contextKey: 'parcel.acres',
    // `resolveSedField` walks `{section}.data.{rest}` — paths follow the
    // SC3 extractor's `{section}.{field}` convention. The `.data.`
    // infix is added by the resolver, not by the binding.
    sedPaths: ['parcel.acres'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Site area (acres)',
  },
  {
    contextKey: 'power.grid',
    sedPaths: ['grid.substationDistKm'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Distance to substation (km)',
  },
  {
    contextKey: 'connectivity.fiber',
    sedPaths: ['connectivity.nearestIxDistKm'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Distance to nearest internet exchange (km)',
  },
  {
    contextKey: 'environmental.wetlands',
    sedPaths: ['environmental.hasWetlands'] as const,
    toSed: identityBoolean,
    toContext: identityBoolean,
    label: 'Wetlands present on parcel',
  },
  {
    contextKey: 'regulatory.zoning',
    // Parcel zoning is the canonical record on the SED. The
    // regulatory section has no zoning slot today; the second path is
    // reserved for future use and is guarded by the allow-list +
    // resolver returning null when the slot is absent.
    sedPaths: ['parcel.zoning'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'Parcel zoning',
  },
  {
    contextKey: 'geotechnical.seismic',
    sedPaths: ['seismic.designCategory'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'Seismic design category',
  },
  // ── IC7 expansion (Input_Layer_Consolidation) ──────────────────
  // 12 additions bring the registry from 6 → 18 claim categories
  // with SED-variable mirrors. Each unlocks SA6's bi-directional
  // sync for that claim → SED path pairing. Free-form categories
  // (regulatory stance prose, schedule narrative) intentionally
  // stay out — they cross-check via the comparator but do not
  // auto-sync because their toContext projection isn't unambiguous.
  {
    contextKey: 'schedule.inservice',
    sedPaths: ['project.inServiceDate'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'In-service date',
  },
  {
    contextKey: 'parcel.boundaries',
    sedPaths: ['parcel.developableAcres'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Developable acres',
  },
  {
    contextKey: 'water.rights',
    sedPaths: ['water.rightsStatus'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'Water rights status',
  },
  {
    contextKey: 'water.source',
    sedPaths: ['water.stressLevel'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'Water stress level',
  },
  {
    contextKey: 'environmental.species',
    sedPaths: ['environmental.speciesConcerns'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'Species concerns',
  },
  {
    contextKey: 'environmental.nepa',
    sedPaths: ['regulatory.nepaPathway'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'NEPA pathway',
  },
  {
    contextKey: 'environmental.tribal',
    sedPaths: ['regulatory.tribalLandProximity'] as const,
    toSed: identityBoolean,
    toContext: identityBoolean,
    label: 'Tribal land proximity',
  },
  {
    contextKey: 'connectivity.latency',
    sedPaths: ['connectivity.latencyMs'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Latency to nearest hyperscaler (ms)',
  },
  {
    contextKey: 'technology.reactor',
    sedPaths: ['technology.data.reactorDesignLabel', 'technology.data.reactorDesignClass'] as const,
    toSed: identityString,
    toContext: identityString,
    label: 'SMR reactor candidate',
  },
  {
    contextKey: 'power.btm',
    sedPaths: ['power.btmCapacityMW'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'BTM generation capacity (MW)',
  },
  {
    contextKey: 'power.timeline',
    sedPaths: ['power.timeToPowerYears'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Time-to-power (years)',
  },
  {
    contextKey: 'finance.cost',
    sedPaths: ['financial.capexTotalUsd'] as const,
    toSed: identityNumber,
    toContext: identityNumber,
    label: 'Total CAPEX (USD)',
  },
] as const;

/**
 * O(1) lookup by category. Built lazily on first access — module
 * top-level so it's hoisted into the JIT-friendly path.
 */
const BINDINGS_BY_CATEGORY: ReadonlyMap<ClaimCategory, readonly SedBindingEntry[]> =
  (() => {
    const m = new Map<ClaimCategory, SedBindingEntry[]>();
    for (const entry of SED_BINDING_REGISTRY) {
      const list = m.get(entry.contextKey) ?? [];
      list.push(entry);
      m.set(entry.contextKey, list);
    }
    return m as ReadonlyMap<ClaimCategory, readonly SedBindingEntry[]>;
  })();

/**
 * O(1) lookup by SED path. A given path can only appear under one
 * entry; we throw on registry author-time conflict via the test, but at
 * runtime we return the FIRST match for resilience.
 */
const BINDINGS_BY_SED_PATH: ReadonlyMap<SedFieldPath, SedBindingEntry> =
  (() => {
    const m = new Map<SedFieldPath, SedBindingEntry>();
    for (const entry of SED_BINDING_REGISTRY) {
      for (const path of entry.sedPaths) {
        if (!m.has(path)) m.set(path, entry);
      }
    }
    return m;
  })();

/** Lookup all bindings that fire for a given claim category. */
export function getBindingsForCategory(
  category: ClaimCategory,
): readonly SedBindingEntry[] {
  return BINDINGS_BY_CATEGORY.get(category) ?? [];
}

/** Lookup the binding (if any) for a SED dot-path. */
export function getBindingForSedPath(
  path: SedFieldPath,
): SedBindingEntry | undefined {
  return BINDINGS_BY_SED_PATH.get(path)
    ?? BINDINGS_BY_SED_PATH.get(variablePathToBindingSedPath(path));
}

/**
 * Map a registry / resolver path (`parcel.acres`) to the SED Variable
 * Browser + amendment path shape (`parcel.data.acres`).
 */
export function bindingSedPathToVariablePath(path: SedFieldPath): string {
  if (path.includes('.data.')) return path;
  const dot = path.indexOf('.');
  if (dot === -1) return path;
  return `${path.slice(0, dot)}.data.${path.slice(dot + 1)}`;
}

/** Inverse of {@link bindingSedPathToVariablePath} for amendment lookups. */
export function variablePathToBindingSedPath(path: string): SedFieldPath {
  if (!path.includes('.data.')) return path as SedFieldPath;
  const [section, , ...rest] = path.split('.');
  if (rest.length === 0) return path as SedFieldPath;
  return `${section}.${rest.join('.')}` as SedFieldPath;
}

/**
 * Convenience for callers that just need to know "is this path bound?".
 * Used by the sync helpers to short-circuit before walking the SED.
 */
export function isBoundSedPath(path: SedFieldPath): boolean {
  return BINDINGS_BY_SED_PATH.has(path);
}

/**
 * Compute the set of SED paths a claim binds to. Used to populate
 * `ExtractedClaim.bindsToSedPaths` after extraction. Pure.
 */
export function computeBindsToSedPaths(
  claim: Pick<ExtractedClaim, 'category'>,
): readonly SedFieldPath[] {
  const entries = getBindingsForCategory(claim.category);
  if (entries.length === 0) return [];
  const out: SedFieldPath[] = [];
  for (const entry of entries) {
    for (const path of entry.sedPaths) {
      if (!out.includes(path)) out.push(path);
    }
  }
  return out;
}
